import { AubadeComponent } from "js/front/bootstrap";
import { gsap } from 'gsap';
import { modulo } from 'libs';
import { modularDistance } from '../../../tools/math/modular-distance';

class Slide {
	constructor(e){
		this.element = e;

		this.visible = false;
		this.active = false;
	}

	get code(){
		return this.element.getAttribute('data-code');
	}

	get visible(){ return this._visible; }
	set visible(visible){
		if(this._visible != visible){
			this._visible = visible;
			this.element.classList.toggle('hidden', !visible);
		}
	}

	get active(){ return this._active; }
	set active(active){
		if(this._active != active){
			this._active = active;
			this.element.classList.toggle('fps-active', active);
		}
	}

	setPosition(x, y){
		this._x = x;
		this._y = y;
		gsap.set(this.element, {
			x: x,
			y: y
		})
	}
}

class SelectorSlideshowDesktop extends AubadeComponent{
	constructor(e, b, name){
		super(e, b);

		this.name = name;

		this.mirror 		 = this.element.getAttribute('data-mirror') == 'true'

		this.circleW		 = this.element.querySelector(".fps-circle-wrapper");
		this.circle			 = this.element.querySelector(".fps-fit-circle");
		this.cursor			 = this.element.querySelector(".fps-selector-cursor");
		this.pointer		 = this.element.querySelector(".fps-product-pointer");
		this.container 		 = this.element.querySelector(".fps-slides-container");

		this.slides = [];

		this.cursorTouchdown = false;
		this.cursorStartCoordinates = {x: 0, y: 0}

		this.globalTouchdown = false;
		this.globalStartCoordinates = {x: 0, y: 0}

		this.startProduct 	= 0;
		this.touchDelta 	= 0;
		this.computedDelta 	= 0;

		this.current 	= 0;
		this.target  	= 0;
		this.lastCode  	= null;

		this.radius	 = this.circle.offsetWidth * 0.5;
		this.decay = 0;

		this.shouldSort = false;

		this._slidesVisible = false;
	}

	attach() {
		this.viewport.on('resize', this.resize);

		if(this.cursor) {
			this.cursor.addEventListener('touchstart', this.cursorTouchStart)
			this.cursor.addEventListener('mousedown', this.cursorTouchStart)
		}

		window.addEventListener('mouseup', this.touchEnd, true)
		window.addEventListener('touchend', this.touchEnd, true)

		window.addEventListener('mousemove', this.touchMove)
		window.addEventListener('touchmove', this.touchMove)
	}

	detach() {
		this.clear();
		this.viewport.off('resize', this.resize);

		if(this.cursor) {
			this.element.removeEventListener('touchstart', this.cursorTouchStart)
			this.element.removeEventListener('mousedown', this.cursorTouchStart)
		}

		window.removeEventListener('touchend', this.touchEnd, true)
		window.removeEventListener('mouseup', this.touchEnd, true)

		window.removeEventListener('mousemove', this.touchMove)
		window.removeEventListener('touchmove', this.touchMove)

	}

	get size(){
		return this.slides.length;
	}

	get infinite(){
		return this.size > 5;	
	}

	get maxDelta(){
		return Math.min(Math.floor(this.size / 2), 4);
	}

	get index(){
		return Math.round(modulo(this.target, this.size) || 0);
	}

	set index(index){
		if(this.infinite){
			this.target += modularDistance(
				this.target,
				index,
				this.size
			);
		}
		else{
			this.target += index - this.target;
		}
		this.target = Math.round(this.target);

		if(this.lastCode != this.code){
			this.emit('change', this.code);
			this.lastCode = this.code;
		}
	}

	get code(){
		return this.slides[this.index].code;
	}
	set code(code){
		let index = this.slides.findIndex(slide => slide.code == code);

		if(this.shouldSort && !this.infinite){
			index = this.sortSlides(Math.max(0, index));
		}

		this.index = Math.max(0, index);
	}

	tick = () => {
		this.renderTimer = requestAnimationFrame(this.tick)

		this.render()
	}

	render = () => {
		this.target 	-= this.touchDelta * 0.0002;
		if(!this.infinite)
			this.target = Math.max(0, Math.min(this.size - 1, this.target));
		this.current 	+= (this.target - this.current) * 0.04;
		this.computedDelta 	+= (this.touchDelta - this.computedDelta) * 0.1;

		this.renderSlides();
		this.renderCursor();
	}

	renderSlides(){
		const index = modulo(this.current, this.size);

		this.slides.forEach((slide, i) => {
			const delta = this.infinite ? modulo(this.current - i + this.maxDelta, this.size) - this.maxDelta : this.current - i;
			const theta = (delta * 0.05) * Math.PI;

			slide.setPosition(
				(1 - Math.cos(theta)) * this.radius * (this.mirror ? -1 : 1),
				(Math.sin(theta)) * this.radius
			);

			slide.visible 	= this.infinite ? Math.abs(modularDistance(i, this.current, this.size)) < 2.2 : Math.abs(i - this.current) < 2.2;
			slide.active 	= Math.round(Math.abs(index)) == i;
		});
	}

	renderCursor(){
		if(!this.cursor || !this.radius) return;

		gsap.set(this.cursor, {
			x: (1 - Math.cos(this.computedDelta / this.radius)) * this.radius,
			y: this.computedDelta,
			rotation: -Math.asin(this.computedDelta / this.radius) / Math.PI * 180
		}, 0);
	}



	fill(items){
		this.clear();

		this.slides = items.map(item => new Slide(item));

		this.slides.forEach((slide, i) => {
			const element = slide.element;
			if(items.length > 1){
				element.addEventListener('click', this.slideClick)
				element.addEventListener('touchstart', this.globalTouchStart)
				element.addEventListener('mousedown', this.globalTouchStart)
			}
			this.container.appendChild(element);
		});

		this.shouldSort = true;
	}

	clear(){
		this.slides.forEach(slide => {
			const element = slide.element;
			element.removeEventListener('click', this.slideClick)
			element.removeEventListener('touchstart', this.globalTouchStart)
			element.removeEventListener('mousedown', this.globalTouchStart)
			this.container.removeChild(element);	
		})
	}


	sortSlides(index){
		const size = this.slides.length;

		if(size > 2){

			const slide = this.slides[index];
			this.slides.splice(index, 1);

			const newIndex = Math.floor(size/2);

			this.slides.splice(newIndex, 0, slide);

			return newIndex;
		}

		return index;
	}


	resize = () => {
		this.w = this.element.offsetWidth
		this.radius = this.circle.offsetWidth * 0.5;
		this.current = this.target;
		this.render();
	}

	getTouchCoordinate(e) {
		if(e.clientX && e.clientY) return {x: e.clientX, y: e.clientY}
		else if(e.changedTouches && e.changedTouches.length) {
			return {x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY}
		}
		return {x: 0, y: 0}
	}
	cursorTouchStart = (e) => {
		if(!this.viewport.medias['desktop']) return
		this.cursorTouchdown = true;
		this.cursorStartCoordinates = this.getTouchCoordinate(e)
		this.startProduct = this.current;
	}
	globalTouchStart = (e) => {
		if(!this.viewport.medias['desktop']) return
		this.globalTouchdown = true;
		this.globalStartCoordinates = this.getTouchCoordinate(e)
		this.startProduct = this.current;
	}
	touchEnd = (e) =>  {
		if(!this.viewport.medias['desktop'] || !this.cursorTouchdown && !this.globalTouchdown) return
		this.cursorTouchdown = false;
		this.globalTouchdown = false;

		this.touchDelta 	= 0;
		this.index = Math.round(this.current);
	}

	touchMove = (e) =>  {
		if(!this.viewport.medias['desktop']) return
		if(this.cursorTouchdown) {

			const maxDelta 	= this.viewport.height / 6;
			const delta 	= (this.getTouchCoordinate(e).y - this.cursorStartCoordinates.y);
			this.touchDelta = Math.min(+maxDelta, Math.max(-maxDelta, delta));

		}
		else if (this.globalTouchdown) {
			this.current = this.target = this.startProduct + (this.getTouchCoordinate(e).y - this.globalStartCoordinates.y) / this.viewport.height * 5;

			if(!this.infinite){
				this.current = Math.max(0, Math.min(this.size - 1, this.target));
			}
		}
	}

	slideClick = (e) => {
		this.index = this.slides.findIndex(slide => slide.element == e.currentTarget);
	}

	getVisibleSlides(){
		return this.slides.filter((slide, i) => slide.visible).sort((a, b) => a._y - b._y);
	}

	open = () => {

		const t = gsap.timeline();

		if(!this._slidesVisible){
			this.resize()
			this.tick()

			if(this.slidesTm)
				this.slidesTm.kill();

			this._slidesVisible = true;

			const slides = this.getVisibleSlides().map(s => s.element);

			if(this.mirror)
				slides.reverse();

			t.set(this.element, {opacity: 1});
			t.fromTo(this.circleW, {rotation:90}, {rotation:0, ease:'quint.out', duration:1.5}, 0);
			t.fromTo(slides, {opacity:0}, {opacity:1, ease:'none', duration:0.5, stagger:0.1}, 0.5);

			if(this.cursor) t.fromTo(this.cursor, {opacity: 0}, {opacity: 1, duration: 0.5}, 1);
			if(this.pointer) t.fromTo(this.pointer, {opacity: 0}, {opacity: 1, duration: 0.5}, 1);

			this.slidesTm = t;
		}
		return t;
	}

	close = () => {

		const t = gsap.timeline();
		if(this._slidesVisible){
			if(this.slidesTm)
				this.slidesTm.kill();

			this._slidesVisible = false;
			const slides = this.getVisibleSlides().map(s => s.element);

			if(!this.mirror)
				slides.reverse();

			
			t.to(this.circleW, {rotation:-90, ease:'quint.out', duration:0.5}, 0);
			t.to(slides, {opacity:0}, {opacity:1, ease:'none', duration:0.5}, 0);
			if(this.cursor) t.to(this.cursor, {opacity: 0, duration: 0.5}, 0);
			if(this.pointer) t.to(this.pointer, {opacity: 0, duration: 0.5}, 0);
			t.to(this.element, {opacity: 0, duration: 0.5}, 0)
			t.add(() => {
				cancelAnimationFrame(this.renderTimer);
			});

			this.slidesTm = t;

		}

		return t;
	}
}

export { SelectorSlideshowDesktop };